import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useConfiguration } from '../Data/ConfigurationData';
import { isLockedApplicationStatus, loanSideDealReadOnlyMessage } from '../utils/postedAppLockUtils';
import { AppState } from '../store/rootReducer';

/** Tooltip message shown on controls disabled by the posted-app lock. */
export const POSTED_APP_LOCK_TOOLTIP =
  'This section is locked because the application has been finalized and posted. An authorized user must unlock it before editing.';

/**
 * Hook for determining if sections of a posted/locked application should be disabled.
 * Uses the application's status and active unlock record to determine per-section editability.
 * A refinance or side note is locked the same way whatever its status, and nothing unlocks it: the LOS opens it
 * read-only because DealerFlow 360 works it (the API enforces the same).
 * The set of locking statuses is the tenant's `LockedApplicationStatuses` configuration item,
 * delivered through `api/configuration`, so the UI matches what the API enforces.
 *
 * @returns Object with:
 *   - isPostedAndLocked: true if the application is in a locked status (no unlock or partial unlock), or is a
 *     read-only refinance or side note
 *   - isReadOnlyLoanSideDeal: true for a refinance or side note, which no unlock opens
 *   - lockMessage: why the locked sections are locked (posted, or worked in DealerFlow 360)
 *   - isSectionLocked: function that checks if a specific section key is locked
 *   - lockedTooltip: function that returns POSTED_APP_LOCK_TOOLTIP when the section is locked, empty string otherwise
 *   - activeUnlock: the active unlock record, if any
 *   - unlockedSections: array of currently unlocked section keys
 */
export function usePostedAppLock() {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const { data: configuration } = useConfiguration();
  const lockedApplicationStatuses = configuration?.lockedApplicationStatuses;

  const loanSideDealMessage = loanSideDealReadOnlyMessage(currentApp?.quoteType);
  const isReadOnlyLoanSideDeal = loanSideDealMessage !== null;
  const lockMessage = loanSideDealMessage ?? POSTED_APP_LOCK_TOOLTIP;

  const isPostedAndLocked = useMemo(
    () => isReadOnlyLoanSideDeal || isLockedApplicationStatus(currentApp?.appStatusId, lockedApplicationStatuses),
    [isReadOnlyLoanSideDeal, currentApp?.appStatusId, lockedApplicationStatuses]);

  const activeUnlock = currentApp?.activeUnlock ?? null;

  const unlockedSections = useMemo<string[]>(() => {
    if (!activeUnlock?.unlockedSections) return [];
    return activeUnlock.unlockedSections;
  }, [activeUnlock]);

  /**
   * Returns true if the given section should be locked (disabled/read-only).
   * Returns false if:
   *   - The app is not in a locked status
   *   - The section is currently unlocked via an active unlock record
   *   - The app was unlocked via the legacy unlockedBySuperUser flag
   */
  const isSectionLocked = useCallback((sectionKey: string): boolean => {
    if (isReadOnlyLoanSideDeal) return true;
    if (!isPostedAndLocked) return false;

    // Legacy full unlock still works
    if (currentApp?.unlockedBySuperUser) return false;

    // If there's an active unlock, check if this section is included
    if (activeUnlock && unlockedSections.length > 0) {
      return !unlockedSections.some(s => s.toLowerCase() === sectionKey.toLowerCase());
    }

    // No unlock — section is locked
    return true;
  }, [isReadOnlyLoanSideDeal, isPostedAndLocked, currentApp?.unlockedBySuperUser, activeUnlock, unlockedSections]);

  /**
   * Returns the lock message for a section if it is locked,
   * or an empty string if it is not locked.
   * Useful as the `title` prop on MUI `<Tooltip>`.
   */
  const lockedTooltip = useCallback((sectionKey: string): string => {
    return isSectionLocked(sectionKey) ? lockMessage : '';
  }, [isSectionLocked, lockMessage]);

  return {
    isPostedAndLocked,
    isReadOnlyLoanSideDeal,
    lockMessage,
    isSectionLocked,
    lockedTooltip,
    activeUnlock,
    unlockedSections,
  };
}
